import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of, shareReplay, switchMap, throwError } from 'rxjs';

import { GET_DYNAMIC_FORM_BY_FEATURE, GET_FEATURES_BY_ROLE } from '../constants/api-endpoints';
import { US_STATES } from '../constants/us-states';
import {
  DynamicFieldResponse,
  DynamicFormEnvelope,
  DynamicFormRequest,
  FeatureItem,
  GetFeaturesByRoleRequest,
  mapDynamicFields
} from '../../models/common/dynamic-form';
import { FormFieldConfig } from '../../shared/components/dynamic-form/form-field.model';
import { BaseSessionService } from './base-session.service';
import { StateService } from './state.service';

/** An option list a field can name via its `lookup`, plus the property names
 *  that turn one of its items into a label and a stored value. */
interface LookupSource {
  options: unknown[];
  optionLabel: string;
  optionValue: string;
  /** Show a search box in the dropdown. */
  filter?: boolean;
}

/** Lookups a configured field can pull its options from. A field naming an
 *  unknown lookup keeps whatever inline `options` it was configured with.
 *  `states` starts from the bundled list and is replaced from
 *  `Master/GetAllStates` before the fields are returned.
 *
 *  NOTE: `fourHClubs` and `ffaClubs` are populated asynchronously via GetClubs
 *  API call in members-roster component — they start empty here and are filled
 *  when the component's `setLookupOptions()` method is called. */
const LOOKUPS: Record<string, LookupSource> = {
  states: { options: US_STATES, optionLabel: 'name', optionValue: 'abbreviation', filter: true },
  fourHClubs: { options: [], optionLabel: 'clubName', optionValue: 'clubUuid' },
  ffaClubs: { options: [], optionLabel: 'clubName', optionValue: 'clubUuid' }
};

/** Cache slot for one feature's configuration in one show. */
function cacheKey(featureName: string, showCode?: string): string {
  return showCode ? `${featureName}|${showCode}` : featureName;
}

/** Form configuration, for every screen that renders itself from the API.
 *
 *  The URL's show segment is always `tsp` — it only satisfies the router, and
 *  the endpoint carries `AllowAnonymousShow`. The show that actually scopes the
 *  mapping travels in the body as `showCode`, so a per-show screen can be
 *  configured differently for `sa` and `hdgs`. The role comes from the JWT, so
 *  the same feature can answer with different fields per role. */
@Injectable({ providedIn: 'root' })
export class DynamicFormService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(BaseSessionService);
  private readonly states = inject(StateService);

  /** One in-flight/replayed request per feature + show, so a screen and its
   *  guard asking at the same time still cost a single call. */
  private readonly fields = new Map<string, Observable<FormFieldConfig[]>>();

  /** Field configuration for a feature, e.g. `FamilyProfile`, in render order
   *  and with `lookup` options already resolved. Pass `showCode` for a screen
   *  that lives inside a show portal; leave it off for a show-agnostic one. */
  getFields(featureName: string, showCode?: string): Observable<FormFieldConfig[]> {
    const key = cacheKey(featureName, showCode);
    const cached = this.fields.get(key);
    if (cached) {
      return cached;
    }

    const request$ = this.resolveFeatureUuid(featureName).pipe(
      switchMap((featureUuid) => this.fetchFields(featureUuid, showCode)),
      switchMap((fields) => this.withStates(fields)),
      map((fields) => fields.map((field) => this.applyLookup(field))),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.fields.set(key, request$);
    return request$;
  }

  /** Drop the cached configuration so the next read re-fetches it. Clears every
   *  show's copy of a feature, since a configuration change is rarely for one. */
  clearFields(featureName?: string): void {
    if (!featureName) {
      this.fields.clear();
      return;
    }
    // Deleting the entry being visited is safe mid-iteration for a Map.
    for (const key of this.fields.keys()) {
      if (key === featureName || key.startsWith(`${featureName}|`)) {
        this.fields.delete(key);
      }
    }
  }

  /** Register or update a lookup's options dynamically.
   *  Used by components like members-roster to populate club lists from GetClubs. */
  registerLookup(lookupName: string, options: unknown[], optionLabel = 'label', optionValue = 'value'): void {
    LOOKUPS[lookupName] = { options, optionLabel, optionValue };
  }

  /** The features a role can open. Cached in session at login so each form's
   *  first paint doesn't pay for this lookup. */
  loadFeatures(roleUuid: string): Observable<FeatureItem[]> {
    const payload: GetFeaturesByRoleRequest = { roleUuid };
    return this.http
      .post<DynamicFormEnvelope<FeatureItem[]>>(GET_FEATURES_BY_ROLE, payload)
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error(response.message ?? 'Failed to fetch features for role');
          }
          this.session.setRoleFeatures(roleUuid, response.data);
          return response.data;
        })
      );
  }

  private resolveFeatureUuid(featureName: string): Observable<string> {
    const cached = this.session.getFeatureByName(featureName);
    if (cached?.featureUuid) {
      return of(cached.featureUuid);
    }

    const roleUuid = this.session.getSessionRoleId();
    if (!roleUuid) {
      return throwError(() => new Error('Role ID not found in session'));
    }

    return this.loadFeatures(roleUuid).pipe(
      map((features) => {
        const feature = features.find((f) => f.featureName === featureName && f.isActive);
        if (!feature) {
          throw new Error(`Feature "${featureName}" is not available for this role`);
        }
        return feature.featureUuid;
      })
    );
  }

  private fetchFields(featureUuid: string, showCode?: string): Observable<FormFieldConfig[]> {
    const payload: DynamicFormRequest = { featureUuid, ...(showCode ? { showCode } : {}) };
    return this.http
      .post<DynamicFormEnvelope<DynamicFieldResponse[]>>(GET_DYNAMIC_FORM_BY_FEATURE('tsp'), payload)
      .pipe(
        map((response) => {
          if (!response.success || !response.data?.length) {
            throw new Error(response.message ?? 'No fields configured for this form');
          }
          return mapDynamicFields(response.data);
        })
      );
  }

  /** Fills a field's options from its named lookup. */
  /** Loads the `states` lookup from the API before it is applied — only when a
   *  field uses it, so forms without a State field cost no extra call. */
  private withStates(fields: FormFieldConfig[]): Observable<FormFieldConfig[]> {
    if (!fields.some((field) => field.lookup === 'states')) {
      return of(fields);
    }
    return this.states.getStates().pipe(
      map((states) => {
        LOOKUPS['states'] = { options: states, optionLabel: 'name', optionValue: 'abbreviation', filter: true };
        return fields;
      })
    );
  }

  private applyLookup(field: FormFieldConfig): FormFieldConfig {
    if (!field.lookup) {
      return field;
    }

    const source = LOOKUPS[field.lookup];
    if (!source) {
      console.warn(`[dynamic-form] Unknown lookup "${field.lookup}" for field "${field.key}"`);
      return field;
    }

    return { ...field, ...source };
  }
}
